import type { CSSProperties } from 'react'
import type { Invite } from '../types'
import { formatNames } from '../guests'
import { weddingInfo } from '../weddingInfo'
import { Countdown } from './Countdown'
import { DietarySection } from './dietary/DietarySection'
import { RoomSection } from './rooms/RoomSection'
import { RsvpCard } from './RsvpCard'
import { WeddingDetails } from './WeddingDetails'

interface InviteViewProps {
  invite: Invite
  onSaved: (invite: Invite) => void
  onSignOut: () => void
}

export function InviteView({ invite, onSaved, onSignOut }: InviteViewProps) {
  const guestNames = invite.people.map((p) => p.name)

  return (
    // Extra room below the footer, so the dog wandering along the bottom of
    // the viewport is never crossing the sign-out link. Padding is split rather
    // than `py-*` so the bottom value is unambiguous.
    <main className="mx-auto max-w-2xl px-4 pt-12 pb-40 sm:pt-16 sm:pb-48">
      <header className="animate-reveal text-center">
        <p className="text-ink-soft text-xs tracking-[0.35em] uppercase">{weddingInfo.dateShort}</p>
        <h1 className="font-display mt-4 text-4xl sm:text-5xl">{weddingInfo.couple}</h1>
        <div className="mt-3">
          <Countdown />
        </div>
        <p className="text-ink-soft mt-6 text-sm">
          {formatNames(guestNames)} — {weddingInfo.intro}
        </p>
      </header>

      <div className="animate-reveal mt-12" style={reveal(140)}>
        <RsvpCard invite={invite} onSaved={onSaved} onSessionLost={onSignOut} />
      </div>

      {/* Food comes after the reply, because it only makes sense once somebody
          has said they are coming. */}
      <div className="animate-reveal mt-16" style={reveal(280)}>
        <DietarySection invite={invite} onSaved={onSaved} onSessionLost={onSignOut} />
      </div>

      {/* Where to sleep is the other thing we need from a guest, so it sits with
          the reply and the food rather than down among the things they only
          read. */}
      <div className="animate-reveal mt-16" style={reveal(360)}>
        <RoomSection invite={invite} onSaved={onSaved} onSessionLost={onSignOut} />
      </div>

      <div className="animate-reveal mt-16" style={reveal(440)}>
        <WeddingDetails people={invite.people} />
      </div>

      <footer className="border-ink/10 mt-16 border-t pt-6 text-center">
        <button
          type="button"
          onClick={onSignOut}
          className="text-ink-soft hover:text-claret text-xs tracking-widest uppercase transition-colors"
        >
          Sign out
        </button>
      </footer>
    </main>
  )
}

/** Staggers the entrance so the page arrives in order rather than all at once. */
function reveal(delayMs: number): CSSProperties {
  return { '--reveal-delay': `${delayMs}ms` } as CSSProperties
}
